import axios from 'axios';

import type {
  FetchPrivacyPolicyPresignedUrlArgs,
  FetchUnsignedPrivacyPolicy,
  FetchPrivacyPoliciesArgs,
  CreatePrivacyPolicyArgs,
  UploadPrivacyPolicyArgs,
} from './types/privacyPolicies.types';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type { ApiPrivacyPolicy } from '../schemas/privacyPolicies.schema';

import { mapPrivacyPolicyFromApi } from './mappers/privacyPolicy.mappers';
import { destroy, patch, post, get } from './axios';

/**
 * Returns URL to upload a document to Cloudfront
 */
export const fetchPrivacyPolicyPresignedUrl = async ({
  language,
}: FetchPrivacyPolicyPresignedUrlArgs) => {
  const response = await get<BaseApiResponse<{ url: string }>>(
    `/v2/privacy-policy/${language}/signed-upload-url`,
  );
  return response?.data?.url;
};

export const fetchPrivacyPolicies = async ({
  filters,
}: FetchPrivacyPoliciesArgs) => {
  const response = await get<BaseApiPaginatedResponse<ApiPrivacyPolicy[]>>(
    '/v2/privacy-policy',
    {
      query: {
        ...filters,
      },
    },
  );
  return {
    ...response,
    data: response?.data?.flatMap((d) => mapPrivacyPolicyFromApi(d)),
  };
};

export const deletePrivacyPolicyById = async ({ id }: { id: string }) => {
  await destroy(`/v2/privacy-policy/${id}`);
  return true;
};

export const createPrivacyPolicy = async ({
  language,
}: CreatePrivacyPolicyArgs) => {
  const response = await post<ApiPrivacyPolicy>(`/v2/privacy-policy`, {
    language: language?.[0]?.value,
  });

  if (!response?.data) return null;

  return mapPrivacyPolicyFromApi(response.data);
};

export const uploadPrivacyPolicy = async ({
  file,
  presignedLink,
}: UploadPrivacyPolicyArgs) => {
  await axios.create().put(presignedLink, file, {
    headers: {
      'Content-Type': 'application/pdf',
    },
  });
  return true;
};

export const fetchUnsignedPrivacyPolicy = async ({
  language,
}: FetchUnsignedPrivacyPolicy) => {
  const urlResponse = await get<BaseApiResponse<{ url: string }>>(
    `/v2/privacy-policy/${language}/signed-read-url`,
  );

  return urlResponse?.data.url;
};

export const acceptPrivacyPolicy = async () => {
  await patch<void>('/v2/auth/me/accept-private-policy');
  return true;
};
